import type { DiscordSDK } from '@discord/embedded-app-sdk';
import { addedFrom, type BringServer, bringList } from '../net/discord';
import { liveLaunch, type LiveLaunch } from '../net/liveMessage';

/**
 * Grand Premio as a Discord Activity. Inside Discord the game runs in a frame served through Discord's proxy
 * (https://<app id>.discordsays.com, every request mapped to the game's own server), and Discord tells it who's
 * playing and in which server. Discord adds `frame_id` (and the instance, platform, server, channel) to the page's
 * address: that's how the game knows it's inside. Everything else in the game is the same.
 */
export const IN_DISCORD = new URLSearchParams(location.search).has('frame_id');

export interface DiscordSession {
  sdk: DiscordSDK;
  /** The game player this Discord account is (the same on every device), and the name it races under. */
  player: { id: string; name: string };
  /** The server it was launched in (checked with Discord), or null (a DM, or a group chat). */
  guild: { id: string; name: string } | null;
  /** The channel it was launched in (where its lap messages go), or null. */
  channel: string | null;
  /** A friend's challenge it was opened from (the game's share link id), or null. */
  linkId: string | null;
  /** Opened from the morning paper's "Read the paper": the player's own paper comes up (again, if read already). */
  paper: boolean;
  /** Opened from the server's "on track now": watching that driver, or racing with them. */
  live: LiveLaunch | null;
  /** The Discord app's id (for its add screen). */
  clientId: string;
  /** Servers the player runs that haven't got the game yet (the card now and then: src/main.ts maybeBring). */
  bring: BringServer[];
  /** Where Discord's add screen comes back to once the manager has chosen the posts' channel (null: none asked for). */
  added: string | null;
}

/** The Discord app's id: the start of the address Discord serves the Activity from, else the server's setting. */
async function clientId(): Promise<string | null> {
  const fromHost = /^(\d{5,25})\.discordsays\.com$/.exec(location.hostname)?.[1];
  if (fromHost) return fromHost;
  try {
    const r = await fetch('api/discord/config');
    return r.ok ? (((await r.json()) as { clientId?: string }).clientId ?? null) : null;
  } catch {
    return null;
  }
}

/**
 * Sign in through Discord: the SDK's handshake, Discord's "who are you" (asked once, then remembered by Discord),
 * the code exchanged by the game's server for a token, and the SDK authenticated with it. `playerId`: this
 * device's player, which a first-time Discord account takes over (so nothing raced before is lost). Null when
 * something along the way fails: the game then runs as a guest, on the global board.
 */
export async function startDiscord(playerId: string): Promise<DiscordSession | null> {
  const id = await clientId();
  if (!id) return null;
  const { DiscordSDK, Common } = await import('@discord/embedded-app-sdk');
  const sdk = new DiscordSDK(id);
  await sdk.ready();
  followThePhone(sdk, Common.OrientationLockStateTypeObject.UNLOCKED);
  const { code } = await sdk.commands.authorize({ client_id: id, response_type: 'code', state: '', prompt: 'none', scope: ['identify', 'guilds'] });
  const r = await fetch('api/discord/token', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code, playerId, guildId: sdk.guildId, channelId: sdk.channelId }),
  });
  if (!r.ok) return null;
  const t = (await r.json()) as {
    access_token: string;
    player: { id: string; name: string };
    guild: { id: string; name: string } | null;
    linkId?: string | null;
    paper?: boolean;
    live?: unknown;
    bring?: unknown;
    added?: unknown;
  };
  await sdk.commands.authenticate({ access_token: t.access_token });
  // A lap to race: from a tap on "Race this lap" in the server (the server kept it), or a shared Activity link.
  const linkId = [t.linkId, sdk.customId].find((x): x is string => typeof x === 'string' && /^[A-Za-z0-9]{10}$/.test(x)) ?? null;
  const channel = sdk.channelId && /^\d{5,25}$/.test(sdk.channelId) ? sdk.channelId : null;
  return { sdk, player: t.player, guild: t.guild, channel, linkId, paper: t.paper === true, live: liveLaunch(t.live), clientId: id, bring: bringList(t.bring), added: addedFrom(t.added) };
}

/**
 * Turning the phone: the game follows it whichever way it's held, full screen, in the small floating window and in
 * the grid beside people's video. (Left unset, Discord went by its own defaults, and a turn sometimes didn't turn the
 * game.) And when Discord says the phone turned or the layout changed, the game measures the screen again a few
 * times over the next second and a half: the frame's new size can arrive after the turn itself. Computers turn
 * nothing: Discord refuses there, quietly.
 */
function followThePhone(sdk: DiscordSDK, unlocked: 1): void {
  void sdk.commands.setOrientationLockState({ lock_state: unlocked, picture_in_picture_lock_state: unlocked, grid_lock_state: unlocked }).catch(() => undefined);
  const remeasure = (): void => {
    for (const ms of [0, 150, 400, 900, 1600]) window.setTimeout(() => window.dispatchEvent(new Event('resize')), ms);
  };
  void sdk.subscribe('ORIENTATION_UPDATE', remeasure).catch(() => undefined);
  void sdk.subscribe('ACTIVITY_LAYOUT_MODE_UPDATE', remeasure).catch(() => undefined);
}

/** An address outside Discord: Discord's own "leaving Discord" prompt, then the browser (a plain link does nothing in the frame). */
export function openOutside(s: DiscordSession, url: string): void {
  void s.sdk.commands.openExternalLink({ url }).catch(() => undefined);
}

/**
 * A challenge inside Discord: Discord's share window with an Activity link that opens the game on this lap (the
 * game's own share link id travels as the link's custom id). Whether it went to someone.
 */
export async function shareChallenge(s: DiscordSession, message: string, linkId: string): Promise<boolean> {
  try {
    const r = await s.sdk.commands.shareLink({ message, custom_id: linkId });
    return r.success;
  } catch {
    return false;
  }
}
